<template>
  <b-col md="12" class="mb-3">
    <b-card class="formCardDash">
      <div class="card-header">
        <h6 class="card-title mb-0">Expense Management</h6>
        <small class="text-muted">Total Expense Amount: {{ formatCurrency(totalExpenseAmount) }} | Total Records: {{ totalRecords }}</small>
      </div>
      <div class="card-body">
        <!-- Filters -->
        <b-row class="mb-3">
          <b-col md="3">
            <b-form-input
              v-model="searchQuery"
              placeholder="Search by invoice no, seller, buyer..."
              @input="debounceSearch"
              size="sm"
            />
          </b-col>
          <b-col md="2">
            <b-form-select
              v-model="perPage"
              :options="[10, 25, 50, 100]"
              @change="updatePagination"
              size="sm"
            />
          </b-col>
          <b-col md="2">
            <b-button
              variant="outline-info"
              size="sm"
              @click="refreshData"
              :disabled="isLoading"
            >
              <i class="fa fa-refresh" :class="{ 'fa-spin': isLoading }"></i>
              Refresh
            </b-button>
          </b-col>
        </b-row>

        <!-- Bootstrap Table with Pagination -->
        <b-table
          :items="expenseData"
          :fields="fields"
          :busy="isLoading"
          striped
          bordered
          hover
          small
          show-empty
          empty-text="No expense data found"
          class="mb-0"
        >
          <template slot="table-busy">
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle" small></b-spinner>
              <strong class="ml-2">Loading...</strong>
            </div>
          </template>

          <template slot="invoiceNo" slot-scope="data">
            <code>{{ data.value }}</code>
          </template>

          <template slot="date" slot-scope="data">
            {{ formatDate(data.value) }}
          </template>

          <template slot="category" slot-scope="data">
            <b-badge variant="secondary">{{ data.value }}</b-badge>
          </template>

          <template slot="sellerName" slot-scope="data">
            <strong>{{ data.value }}</strong>
            <br>
            <small class="text-muted">{{ data.item.sellerTradeName }}</small>
          </template>

          <template slot="buyerName" slot-scope="data">
            <strong>{{ data.value }}</strong>
            <br>
            <small class="text-muted">{{ data.item.buyerTradeName }}</small>
          </template>

          <template slot="amount" slot-scope="data">
            <strong class="text-right d-block">{{ formatCurrency(data.value) }}</strong>
          </template>

          <template slot="status" slot-scope="data">
            <b-badge :variant="data.value === 'Active' ? 'success' : 'danger'">
              {{ data.value }}
            </b-badge>
          </template>

          <template slot="items" slot-scope="data">
            <b-badge variant="primary">{{ data.value.length || 0 }}</b-badge>
          </template>

          <template slot="jobDetails.JobNo" slot-scope="data">
            <code v-if="data.value">{{ data.value }}</code>
            <span v-else class="text-muted">N/A</span>
          </template>

          <template slot="actions" slot-scope="data">
            <b-button
              size="sm"
              variant="outline-info"
              @click.stop="toggleDetails(data.item)"
              class="toggle-btn"
            >
              <i :class="data.item._showDetails ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
            </b-button>
          </template>

          <!-- Row Details Slot -->
          <template slot="row-details" slot-scope="row">
            <div class="row-details">
              <!-- Expense Details -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-primary">
                    <div class="card-header bg-primary text-white">
                      <i class="fa fa-info-circle"></i> Expense Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3">
                          <strong>Expense ID:</strong> {{ row.item.expenseId }}
                        </div>
                        <div class="col-md-3">
                          <strong>Invoice No:</strong> {{ row.item.invoiceNo }}
                        </div>
                        <div class="col-md-3">
                          <strong>Category:</strong> {{ row.item.category }}
                        </div>
                        <div class="col-md-3">
                          <strong>Status:</strong>
                          <b-badge :variant="row.item.status === 'Active' ? 'success' : 'danger'" class="ml-2">
                            {{ row.item.status }}
                          </b-badge>
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-12">
                          <strong>Description:</strong> {{ row.item.description }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Job Details (if exists) -->
              <div class="row mb-3" v-if="row.item.jobDetails">
                <div class="col-12">
                  <div class="card border-success">
                    <div class="card-header bg-success text-white">
                      <i class="fa fa-briefcase"></i> Job Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3">
                          <strong>Job No:</strong> {{ row.item.jobDetails.JobNo }}
                        </div>
                        <div class="col-md-3">
                          <strong>Job Date:</strong> {{ formatDate(row.item.jobDetails.JobDate) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Job Type:</strong> {{ row.item.jobDetails.JobType }}
                        </div>
                        <div class="col-md-3">
                          <strong>Transport Type:</strong> {{ row.item.jobDetails.JobTransportType }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>Client:</strong> {{ row.item.jobDetails.ClientName }}
                        </div>
                        <div class="col-md-3">
                          <strong>Shipping Line:</strong> {{ row.item.jobDetails.SlineName }}
                        </div>
                        <div class="col-md-3">
                          <strong>Raised By:</strong> {{ row.item.jobDetails.RaisedBy }}
                        </div>
                        <div class="col-md-3">
                          <strong>Raised From:</strong> {{ row.item.jobDetails.RaisedFrom }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>POL → POD:</strong> {{ row.item.jobDetails.POL }} → {{ row.item.jobDetails.POD }}
                        </div>
                        <div class="col-md-3">
                          <strong>Container Type:</strong> {{ row.item.jobDetails.JobContainerType }}
                        </div>
                        <div class="col-md-3">
                          <strong>Enquiry:</strong> {{ row.item.jobDetails.EnqNo }}
                        </div>
                        <div class="col-md-3">
                          <strong>SOB:</strong> {{ row.item.jobDetails.SOB }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Expense Items -->
              <div class="row mb-3" v-if="row.item.items && row.item.items.length > 0">
                <div class="col-12">
                  <div class="card border-danger">
                    <div class="card-header bg-danger text-white">
                      <i class="fa fa-list"></i> Expense Items ({{ row.item.items.length }})
                    </div>
                    <div class="card-body p-0">
                      <b-table
                        :items="row.item.items"
                        :fields="itemFields"
                        small
                        bordered
                        class="mb-0"
                      >
                        <template slot="amount" slot-scope="data">
                          {{ formatCurrency(data.value) }}
                        </template>
                      </b-table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Modules -->
              <div class="row mb-3" v-if="row.item.modules && row.item.modules.length > 0">
                <div class="col-12">
                  <div class="card border-info">
                    <div class="card-header bg-info text-white">
                      <i class="fa fa-cubes"></i> Modules ({{ row.item.modules.length }})
                    </div>
                    <div class="card-body p-0">
                      <b-table
                        :items="row.item.modules"
                        :fields="moduleFields"
                        small
                        bordered
                        class="mb-0"
                      >
                        <template slot="amount" slot-scope="data">
                          {{ formatCurrency(data.value) }}
                        </template>
                      </b-table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <b-pagination
          v-if="totalItems > pagination.perPage"
          limit="25"
          align="center"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total-rows="totalItems"
          @change="onPageChange"
          class="mt-3"
        ></b-pagination>

        <!-- Table Info -->
        <div class="mt-2 d-flex justify-content-between align-items-center">
          <div>
            <b-form-select
              v-model="pagination.perPage"
              :options="pagination.pageOptions"
              @change="updatePagination"
              size="sm"
              class="w-auto"
            ></b-form-select>
            <span class="ml-2 text-muted">per page</span>
          </div>

          <div class="text-muted small">
            Total Records: {{ totalItems }} | Total Amount: {{ formatCurrency(totalExpenseAmount) }}
          </div>
        </div>

      </div>
    </b-card>
  </b-col>
</template>

<script>
import moment from "moment";

export default {
  name: "ExpenseSection",
  props: {
    expenseData: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      searchQuery: '',
      searchTimeout: null,
      isLoading: false,
      totalItems: 0,
      totalPages: 1,
      pagination: {
        currentPage: 1,
        perPage: 10,
        pageOptions: [10, 25, 50, 100],
      },
      fields: [
        { key: 'invoiceNo', label: 'Invoice No', sortable: true },
        { key: 'date', label: 'Date', sortable: true },
        { key: 'category', label: 'Category', sortable: false },
        { key: 'sellerName', label: 'Seller', sortable: true },
        { key: 'buyerName', label: 'Buyer', sortable: true },
        { key: 'description', label: 'Description', sortable: false },
        { key: 'amount', label: 'Amount', sortable: true, class: 'text-right' },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'items', label: 'Items', sortable: false, class: 'text-center' },
        { key: 'jobDetails.JobNo', label: 'Job No', sortable: false },
        { key: 'actions', label: 'Actions', sortable: false, class: 'text-center' }
      ],
      itemFields: [
        { key: 'slNo', label: 'SL No', class: 'text-center' },
        { key: 'description', label: 'Description' },
        { key: 'amount', label: 'Amount', class: 'text-right' },
        { key: 'gstRate', label: 'GST Rate', class: 'text-right' },
        { key: 'sgstAmount', label: 'SGST', class: 'text-right' },
        { key: 'igstAmount', label: 'IGST', class: 'text-right' },
        { key: 'cgstAmount', label: 'CGST', class: 'text-right' }
      ],
      moduleFields: [
        { key: 'name', label: 'Module Name' },
        { key: 'amount', label: 'Amount', class: 'text-right' }
      ]
    };
  },
  computed: {
    totalExpenseAmount() {
      return this.expenseData.reduce((sum, expense) => sum + (expense.amount || 0), 0);
    },

    totalRecords() {
      return this.totalItems;
    },
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return "₹0";
      return "₹" + Number(value).toLocaleString("en-IN");
    },

    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD-MM-YYYY");
    },

    toggleDetails(expense) {
      // Toggle the _showDetails property for BootstrapVue row-details
      expense._showDetails = !expense._showDetails;
      this.$forceUpdate(); // Force update to trigger reactivity
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.filter = this.searchQuery;
      }, 500);
    },

    updatePagination() {
      this.pagination.currentPage = 1;
    },

    onFiltered(filteredItems) {
      // Trigger pagination reset when filtered
      this.pagination.currentPage = 1;
    },

    onPageChange(page) {
      this.pagination.currentPage = page;
      this.loadData();
    },

    async loadData() {
      this.isLoading = true;
      try {
        const payload = {
          salesPersonId: this.$store.state.user.userId,
          clientId: this.$store.state.client.clientId || null,
          dateRange: this.$store.state.dateRange || '2025-2026',
          page: this.pagination.currentPage,
          limit: this.pagination.perPage,
          search: this.searchQuery
        };

        // Emit event to parent to load expense data
        this.$emit('load-expense-data', payload);
      } catch (error) {
        console.error('Error loading expense data:', error);
        this.isLoading = false;
      }
    },

    refreshData() {
      this.loadData();
    },
  },

  mounted() {
    this.loadData();
  },

  watch: {
    expenseData: {
      handler(newData) {
        // Initialize _showDetails property for each item
        if (newData && newData.length > 0) {
          newData.forEach(item => {
            if (item._showDetails === undefined) {
              this.$set(item, '_showDetails', false);
            }
          });
        }
      },
      immediate: true,
      deep: true
    }
  }
};
</script>

<style scoped>
.formCardDash {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.expanded-details {
  background-color: #f8f9fa;
  border-top: 2px solid #dee2e6;
  padding: 15px;
}

.expanded-details .row {
  margin-bottom: 10px;
}

.expanded-details h6 {
  margin-bottom: 10px;
  font-weight: 600;
}

.expanded-details table.table-sm {
  font-size: 12px;
}

.expanded-details table.table-sm td {
  padding: 4px 8px;
  border: none;
}

.table th {
  position: sticky;
  top: 0;
  background-color: #007bff !important;
  color: white !important;
  z-index: 10;
  border-color: #0056b3;
  font-weight: 600;
  text-align: center;
  vertical-align: middle;
  padding: 12px 8px;
}

.table thead th {
  border-bottom: 2px solid #0056b3;
}

.table tbody td {
  vertical-align: middle;
  padding: 8px;
}

.table tbody td.text-center {
  text-align: center;
}

.table tbody td.text-right {
  text-align: right;
}

.badge {
  font-size: 11px;
}

.btn-outline-info {
  border-color: #17a2b8;
  color: #17a2b8;
}

.btn-outline-info:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.toggle-btn {
  min-width: 40px;
  transition: all 0.2s ease;
}

.toggle-btn:hover {
  transform: scale(1.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .expanded-details .col-md-6 {
    margin-bottom: 20px;
  }

  .table-responsive {
    max-height: 400px;
  }
}
</style>


// WEBPACK FOOTER //
// ExpenseSection.vue?e1e369a4